import * as React from 'react'

import {
  ListitemSeparator,
  StyledList,
} from '@/components/ProductDetailsSpecificationsList/ProductDetailsSpecifications.styled'
import { SpecificationsListItem } from '@/components/ProductDetailsSpecificationsList/SpecificationsListItem'
import { ProductClassificationEnum } from '@/enums/ProductClassificationEnum'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { useTranslations } from '@/hooks/useTranslations'

import type { ProductProps } from '@/interfaces/product.interface'

interface Props {
  product: ProductProps
  salesUnitDescription: string
  packagingDescription: string
}

export const ProductDetailsSpecifications: React.FC<Props> = ({
  product,
  salesUnitDescription,
  packagingDescription,
}) => {
  const { translate } = useTranslations()
  const showSailabCode = useFeatureFlag('sailabCode')
  const getValueByKey = React.useCallback(
    (key: string): string | null => {
      if (product?.mediqClassifications == null || product?.mediqClassifications.length === 0) {
        return null
      }

      const specificationKey = product?.mediqClassifications.find(
        (mediqClassificationsKey) => mediqClassificationsKey?.code === key
      )
      const specification = specificationKey?.values

      if (specification != null && specification?.length > 0) {
        switch (specification.find(Boolean)) {
          case 'true':
            return translate('productDetails.yes')?.toString() ?? 'Yes'
          case 'false':
            return translate('productDetails.no')?.toString() ?? 'No'

          default:
            return specification.find(Boolean) ?? null
        }
      } else {
        return null
      }
    },
    [product?.mediqClassifications, translate]
  )

  return (
    <StyledList>
      <SpecificationsListItem listItem={product?.code} labelItem={translate('productDetails.itemNumber')} />
      <SpecificationsListItem
        listItem={showSailabCode ? product?.sailabCode : null}
        labelItem={translate('product.sailabCode')}
      />
      <SpecificationsListItem
        listItem={salesUnitDescription}
        labelItem={translate('productDetails.productSalesUnit')}
      />
      <SpecificationsListItem listItem={packagingDescription} labelItem={translate('product.packagingDescription')} />
      <SpecificationsListItem
        listItem={getValueByKey(ProductClassificationEnum.color)}
        labelItem={translate('productDetails.color')}
      />
      <SpecificationsListItem
        listItem={getValueByKey(ProductClassificationEnum.subBrand)}
        labelItem={translate('productDetails.subBrand')}
      />
      <SpecificationsListItem
        listItem={getValueByKey(ProductClassificationEnum.brand)}
        labelItem={translate('productDetails.brand')}
      />
      <SpecificationsListItem listItem={null} labelItem={translate('productDetails.manufacturerNo')} />
      <SpecificationsListItem listItem={product?.manufacturer} labelItem={translate('productDetails.manufacturer')} />
      <SpecificationsListItem
        listItem={product?.supplierAlternativeAID}
        labelItem={translate('productDetails.supplierItemNumber')}
      />
      <SpecificationsListItem listItem={product?.ean} labelItem={translate('productDetails.eanNo')} />
      <ListitemSeparator />
      <SpecificationsListItem
        listItem={getValueByKey(ProductClassificationEnum.depth)}
        labelItem={translate('productDetails.depth')}
      />
      <SpecificationsListItem
        listItem={getValueByKey(ProductClassificationEnum.width)}
        labelItem={translate('productDetails.width')}
      />
      <SpecificationsListItem
        listItem={getValueByKey(ProductClassificationEnum.height)}
        labelItem={translate('productDetails.height')}
      />
      <SpecificationsListItem
        listItem={getValueByKey(ProductClassificationEnum.grossWeight)}
        labelItem={translate('productDetails.grossWidth')}
      />
      <SpecificationsListItem
        listItem={getValueByKey(ProductClassificationEnum.netWight)}
        labelItem={translate('productDetails.netWidth')}
      />
      <SpecificationsListItem
        listItem={getValueByKey(ProductClassificationEnum.grossVolume)}
        labelItem={translate('productDetails.grossVolume')}
      />
      <SpecificationsListItem
        listItem={getValueByKey(ProductClassificationEnum.netVolume)}
        labelItem={translate('productDetails.netVolume')}
      />
      <SpecificationsListItem listItem={product?.size} labelItem={translate('productDetails.productSize')} />
      <SpecificationsListItem
        listItem={
          product.ceMarking
            ? translate('productDetails.product.ce_marked.yes')
            : translate('productDetails.product.ce_marked.no')
        }
        labelItem={translate('productDetails.product.ce_marked')}
      />

      <ListitemSeparator />
      {product.regulationNotifiedBody != null && (
        <SpecificationsListItem
          listItem={product.regulationNotifiedBody}
          labelItem={translate('productDetails.regulationNotifiedBody')}
        />
      )}
      {product.regulationDirective != null && (
        <SpecificationsListItem
          listItem={product.regulationDirective}
          labelItem={translate('productDetails.regulationDirective')}
        />
      )}
      {product.regulationProductClass != null && (
        <SpecificationsListItem
          listItem={product.regulationProductClass}
          labelItem={translate('productDetails.regulationProductClass')}
        />
      )}
      {product.regulationProductClass != null ||
      product.regulationDirective != null ||
      product.regulationNotifiedBody != null ? (
        <ListitemSeparator />
      ) : null}

      <SpecificationsListItem listItem={null} labelItem={translate('productDetails.ceCertificate')} />
      <SpecificationsListItem listItem={null} labelItem={translate('productDetails.ceCertificateDate')} />
      <SpecificationsListItem
        listItem={getValueByKey(ProductClassificationEnum.isSterile)}
        labelItem={translate('productDetails.isSterile')}
      />
      <SpecificationsListItem
        listItem={getValueByKey(ProductClassificationEnum.containsLatex)}
        labelItem={translate('productDetails.containsLatex')}
      />
      <SpecificationsListItem
        listItem={getValueByKey(ProductClassificationEnum.containsPvc)}
        labelItem={translate('productDetails.containsPVC')}
      />
    </StyledList>
  )
}
